import { css } from '@emotion/react'
import { Tabs } from 'bold-ui'
import { PecSwitch, PrivateRoute, TabLink } from 'components/route'
import { AtendimentoProfissional, FonteIdadeGestacionalEnum } from 'graphql/types.generated'
import { Fragment } from 'react'
import { Redirect, useLocation, useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions'
import { grupoCboVisualizarAcompanhamentos } from 'view/cidadao/authorization/acessos'

import { usePeriodoGestacional } from '../../soap/aside/pre-natal/hooks/usePeriodoGestacional'
import {
  AcompanhamentoPreNatalCardQueryModel,
  AcompanhamentoPreNatalQueryModel,
} from '../../soap/aside/pre-natal/model-acompanhamentoPreNatal'
import { ExternalPaths } from '../model-acompanhamentos'
import { InformacoesGestacaoTabContent } from './components/InformacoesGestacaoTabContent'
import { MedicoesGestacaoTabContent } from './components/MedicoesGestacaoTabContent'

export const INFO_GESTACAO_URL = 'informacoes'
const MEDICOES_GESTACAO_URL = 'medicoes'

interface AcompanhamentoPreNatalTabsProps {
  cidadaoId: ID
  prontuarioId: ID
  atendimentoId: ID
  dataReferencia: Instant
  externalPaths: ExternalPaths
  isVisualizacaoProntuario: boolean
  isEscutaInicial: boolean
  atendimentosProfUltimaGestacao: ReadonlyArray<AcompanhamentoPreNatalQueryModel | AcompanhamentoPreNatalCardQueryModel>
  loadingAtendimentos: boolean
  dum: Date
}

export const AcompanhamentoPreNatalTabs = (props: AcompanhamentoPreNatalTabsProps) => {
  const {
    cidadaoId,
    prontuarioId,
    atendimentoId,
    dataReferencia,
    externalPaths,
    isVisualizacaoProntuario,
    isEscutaInicial,
    atendimentosProfUltimaGestacao,
    loadingAtendimentos,
    dum,
  } = props

  const match = useRouteMatch()
  const location = useLocation()

  const informacoesPath = `${match.url}/${INFO_GESTACAO_URL}${location.search}`
  const medicoesPath = `${match.url}/${MEDICOES_GESTACAO_URL}${location.search}`

  const {
    loading: loadingPeriodoGestacional,
    idadeGestacional,
    dataProvavelPartoEcografica: dppEcografica,
    dataInicioGestacao,
    fonteIdadeGestacional,
    atendimentosPeriodoGestacional,
  } = usePeriodoGestacional({
    prontuarioId: prontuarioId,
    dum,
    dataAtendimento: dataReferencia,
    atendimentosProfUltimaGestacao,
  })

  const atendimentosProfissionaisUltimaGestacao = atendimentosPeriodoGestacional as AtendimentoProfissional[]

  const renderInformacoesGestacao = () => (
    <InformacoesGestacaoTabContent
      cidadaoId={cidadaoId}
      prontuarioId={prontuarioId}
      atendimentoId={atendimentoId}
      dataReferencia={dataReferencia}
      loading={loadingAtendimentos || loadingPeriodoGestacional}
      dum={dum}
      idadeGestacional={idadeGestacional}
      dataInicioGestacao={dataInicioGestacao}
      fonteIdadeGestacional={fonteIdadeGestacional}
      dppEcografica={dppEcografica}
      atendimentosProfUltimaGestacao={atendimentosProfissionaisUltimaGestacao}
      medicoesPath={medicoesPath}
      externalPaths={externalPaths}
      isVisualizacaoProntuario={isVisualizacaoProntuario}
      isEscutaInicial={isEscutaInicial}
    />
  )

  const renderMedicoes = () => (
    <MedicoesGestacaoTabContent
      cidadaoId={cidadaoId}
      prontuarioId={prontuarioId}
      dataInicioGestacao={dataInicioGestacao}
      atendimentosProfUltimaGestacao={atendimentosProfissionaisUltimaGestacao}
      isDataInicioGestacaoEcografica={idadeGestacional?.fonteIdadeGestacional === FonteIdadeGestacionalEnum.US}
      loading={loadingAtendimentos || loadingPeriodoGestacional}
      externalPaths={externalPaths}
      isVisualizacaoProntuario={isVisualizacaoProntuario}
      isEscutaInicial={isEscutaInicial}
    />
  )

  return (
    <Fragment>
      <Tabs style={styles.tabs}>
        <TabLink to={informacoesPath}>Informações da gestação</TabLink>
        <TabLink to={medicoesPath}>Medições</TabLink>
      </Tabs>
      <PecSwitch>
        <Redirect exact from={match.url} to={informacoesPath} />
        <PrivateRoute
          path={`${match.url}/${INFO_GESTACAO_URL}`}
          render={renderInformacoesGestacao}
          permission={Permissions.visualizarProntuario}
          cbos={grupoCboVisualizarAcompanhamentos}
        />
        <PrivateRoute
          path={`${match.url}/${MEDICOES_GESTACAO_URL}`}
          render={renderMedicoes}
          permission={Permissions.visualizarProntuario}
          cbos={grupoCboVisualizarAcompanhamentos}
        />
      </PecSwitch>
    </Fragment>
  )
}

const styles = {
  tabs: css`
    margin-bottom: 1rem;
  `,
}
